import { useEffect, useState } from 'react';
import Api from '../api';
import { RegionalFreePeriodEligibility } from '../api/Api';

export interface RegionalFreePeriodState {
    eligibility: RegionalFreePeriodEligibility | null;
    loading: boolean;
    error: boolean;
}

interface RegionalFreePeriodRequestState extends RegionalFreePeriodState {
    requestKey: string | null;
}

export default function useRegionalFreePeriod(
    locationId?: number | string | null,
    date?: string | null,
): RegionalFreePeriodState {
    const normalizedId = Number(locationId);
    const validLocationId = normalizedId && !Number.isNaN(normalizedId)
        ? normalizedId
        : null;
    const normalizedDate = date || null;
    const requestKey = validLocationId
        ? `${validLocationId}:${normalizedDate || ''}`
        : null;
    const [state, setState] = useState<RegionalFreePeriodRequestState>({
        eligibility: null,
        loading: false,
        error: false,
        requestKey: null,
    });

    useEffect(() => {
        let active = true;
        if (!validLocationId || !requestKey) {
            setState({ eligibility: null, loading: false, error: false, requestKey: null });
            return () => { active = false; };
        }

        setState({ eligibility: null, loading: true, error: false, requestKey });
        Api.getRegionalFreePeriodEligibility(validLocationId, normalizedDate, 'placement')
            .then(eligibility => {
                if (active) setState({ eligibility, loading: false, error: false, requestKey });
            })
            .catch(() => {
                // Eligibility failures must fail closed: the regular fee remains
                // visible and no promotional promise is made.
                if (active) setState({ eligibility: null, loading: false, error: true, requestKey });
            });

        return () => { active = false; };
    }, [normalizedDate, requestKey, validLocationId]);

    if (!requestKey) {
        return { eligibility: null, loading: false, error: false };
    }

    // Effects clear the previous response after a changed location/date renders.
    // Key the visible state as well so React never paints old campaign pricing for
    // the new selection during that pre-effect render.
    if (state.requestKey !== requestKey) {
        return { eligibility: null, loading: true, error: false };
    }

    return {
        eligibility: state.eligibility,
        loading: state.loading,
        error: state.error,
    };
}
